import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    SearchParameters,
    useSearchParameters
} from "@/components/signup/hooks";
import {
    UfoCustomTimingKey,
    ufoEventConfirmationPageStatusPolling,
    ufoEventGraphQLGetProvisioningStatus,
    ufoEventGraphQLSubscriptionSetup,
    ufoEventRedirectToProduct,
    ProductKey,
    getIdentityBaseUrl
} from "@/components/signup/utils";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/hook";
import { GRAPHQL_FALLBACK_TIMEOUT } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/utils/types";
import { fireFrontendProvisioningCompletedEvent } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/utils/use-cloud-status-utils.ts";
import { trackGetProvisioningStatusLatencyEvent } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics.ts";
import { getDataFoundationEvent } from "@/utils/data-foundations/data-foundation-types.tsx";
import { useFeatureExposed } from "@/utils/feature-exposed";
import { graphqlSubscriptionsQueries } from "@/utils/graphql/graphql-queries.ts";
import { useGraphQlSubscription } from "@/utils/graphql/useGraphqlSubscription.tsx";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics/analytics-client.tsx";

import { createBaseUrl, pollCloudStatus, measureTime } from "./util";

export type SignupProvisioningStatus =
    | "pending"
    | "success"
    | "timeout"
    | "error";

export type CCPProvisioningData = {
    cloudId?: string;
};

type UrlModifier = (url: URL) => URL;
export interface SignupProvisioning {
    startTime: number;
    status: SignupProvisioningStatus;
    parameters: SearchParameters;
    createRedirectUrl: (urlModifier?: UrlModifier) => string;
    ccpData: CCPProvisioningData;
    readonly perceivedProvisioningStartTime?: number | undefined;
    readonly setPerceivedProvisioningStartTime: (
        startTimeSeconds: number | undefined
    ) => void;
    onSuccess: (
        startTime: number,
        endTime: number,
        elapsedTime: number
    ) => void;
    onError: (
        startTime: number,
        endTime: number,
        elapsedTime: number,
        error: unknown
    ) => void;
    onTimeout: (
        startTime: number,
        endTime: number,
        elapsedTime: number
    ) => void;
}

const {
    ACTIVATE_STATUS_CCP_POLLING_API,
    CLOUD_STATUS_HAMS_POLLING_API,
    GRAPHQL_SUBSCRIPTION_ACK,
    GRAPHQL_WEBHOOK_PROVISIONING_LATENCY
} = UfoCustomTimingKey;

const SignupProvisioningContext = createContext<SignupProvisioning | null>(
    null
);

export const useSignupProvisioningContext = (): SignupProvisioning => {
    const context = useContext(SignupProvisioningContext);
    if (!context) {
        throw new Error(
            "useSignupProvisioningContext must be used within a SignupProvisioningContextProvider"
        );
    }
    return context;
};

export const SignupProvisioningContextProvider = ({
    children
}: {
    children: ReactNode;
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const searchParameters = useSearchParameters();
    const stargateClient = useBxpWebStargateClient();
    const analyticsClient = useAnalyticsClient();

    useFeatureExposed("jfind-1245-confluence-streamline-redirect", "test");

    const {
        cloudId,
        onDemandUrl,
        platform,
        products,
        provisioningRequestId,
        requestId,
        siteRenameRequestId,
        signupSource,
        isFoundingAdmin,
        dataRegion,
        admin,
        orchestrationId,
        edition
    } = searchParameters;
    const productKey = useMemo(
        () => (products.length > 0 ? products[0] : undefined) as ProductKey,
        [products]
    );

    const hasRequiredParameters = useMemo(
        () => !!onDemandUrl && !!products.length,
        [onDemandUrl, products]
    );

    const [startTime] = useState(Date.now());
    const currentTime = Date.now();
    const [status, setStatus] = useState<SignupProvisioningStatus>(
        !hasRequiredParameters ? "error" : "pending"
    );
    const graphqlProvisioningMS = useRef(0);
    const [enrolledInGraphqlExperiment, setEnrolledInGraphqlExperiment] =
        useState<boolean>(orchestrationId ? true : false);
    const [startPolling, setStartPolling] = useState<boolean>(false);
    const [graphqlProvisionComplete, setGraphqlProvisionComplete] =
        useState<boolean>(false);
    const provisionPollingTimes = useRef<number>(0);
    const [ccpProvisioningCloudId, setCCPProvisioningCloudId] = useState("");
    const [perceivedProvisioningStartTime, setPerceivedProvisioningStartTime] =
        useState<number | undefined>(undefined);

    // Start time for perceived provisioning based on time spent on loading screens (dwell time)
    const perceivedProvisioningStartTimeRef = useRef<number>();
    useEffect(() => {
        perceivedProvisioningStartTimeRef.current =
            perceivedProvisioningStartTime;
    }, [perceivedProvisioningStartTime]);

    const setCloudIdIfPresent = (cloudId?: string) => {
        cloudId?.length && setCCPProvisioningCloudId(cloudId);
    };

    const createRedirectUrl = useCallback(
        (urlModifier?: UrlModifier) => {
            let url: URL = new URL(
                createBaseUrl(
                    onDemandUrl,
                    products,
                    dataRegion,
                    cloudId,
                    admin,
                    createAnalyticsEvent
                )
            );
            createAnalyticsEvent({
                ...getDataFoundationEvent("ProductNavigationInitialized"),
                actionSubjectId: "newSite",
                attributes: {
                    url,
                    productKey,
                    edition
                }
            }).fire();

            // This causes browser to retry for 30 seconds if site isn't ready
            url.searchParams.set("src", "signup");

            if (siteRenameRequestId) {
                url.searchParams.set("rename", "true");
            }
            if (signupSource === "bbbundle" && isFoundingAdmin === "true") {
                url.searchParams.set("signupSource", signupSource);
                url.searchParams.set("isFoundingAdmin", isFoundingAdmin);
            }

            if (urlModifier) {
                url = urlModifier(url);
            }

            return `${getIdentityBaseUrl()}/login?${new URLSearchParams({
                continue: url.toString(),
                application: "confluence"
            })}`;
        },
        [
            onDemandUrl,
            products,
            dataRegion,
            cloudId,
            admin,
            createAnalyticsEvent,
            siteRenameRequestId,
            signupSource,
            isFoundingAdmin
        ]
    );

    // TODO: Add test coverage based on query params
    /* istanbul ignore next */
    const getProvisioningCloudStatus = useCallback(async () => {
        provisionPollingTimes.current = provisionPollingTimes.current + 1;
        if (platform === "ccp") {
            if (!provisioningRequestId) return false;
            const getProductActivationStatus = async (product: string) => {
                return measureTime(
                    () =>
                        stargateClient.getProductActivationStatus({
                            provisioningRequestId: product,
                            provisionPollingTimes: provisionPollingTimes.current
                        }),
                    `getProductActivationStatus`,
                    createAnalyticsEvent
                );
            };
            const requestIds = await Promise.all(
                provisioningRequestId.split(",").map(async (product) => {
                    try {
                        return await getProductActivationStatus(product);
                    } catch (error) {
                        // @ts-ignore
                        if (error.response?.status === 400) {
                            ufoEventRedirectToProduct.abort({
                                customData: {
                                    reactComponent:
                                        "SignupProvisioningContextProvider",
                                    analyticsEvent: "redirect",
                                    redirectFrom: "confirmationPage",
                                    errorOrigin: "StaleProvisioningRedirect"
                                },
                                error
                            });
                            window.location.assign(createRedirectUrl());
                        }
                        // retry if the first call fails
                        return await getProductActivationStatus(product);
                    }
                })
            );
            // Get cloud id from provisioning status, since ccp sign up
            // doesn't provide cloud id query parameter for provisioning screen
            const cloudId = requestIds[0]?.resourcePackages[0]?.resourceAris
                .find((resourceAri) =>
                    resourceAri.includes("ari:cloud:platform::site")
                )
                ?.split("/")[1];
            setCloudIdIfPresent(cloudId);
            for (const resource of requestIds) {
                if (
                    resource.resourcePackages.find(
                        (item) => item.action.status === "IN_PROGRESS"
                    )
                )
                    return false;
                if (
                    resource.resourcePackages.find(
                        (item) => item.action.status === "PROVISIONING_FAILURE"
                    )
                ) {
                    return Promise.reject(
                        new Error(
                            `Provisioning Failure CCP with cloudId ${cloudId}: ${JSON.stringify(
                                resource.resourcePackages[0]?.action
                                    .errorDetails
                            )}`
                        )
                    );
                }
            }
            return true;
        } else {
            if (!cloudId || !requestId) return false;
            setCloudIdIfPresent(cloudId);
            const getCloudStatus = async () => {
                return measureTime(
                    async () => {
                        const { completed, successful } =
                            await stargateClient.getCloudStatus({
                                cloudId,
                                requestId
                            });
                        return completed && successful;
                    },
                    "getCloudStatus",
                    createAnalyticsEvent
                );
            };
            try {
                return await getCloudStatus();
            } catch {
                return await getCloudStatus();
            }
        }
    }, [
        platform,
        provisioningRequestId,
        cloudId,
        requestId,
        stargateClient,
        createRedirectUrl
    ]);

    const {
        data: subscriptionData,
        loading: subscriptionLoading,
        error: subscriptionError
    } = useGraphQlSubscription({
        reactComponent: "SignupProvisioningContextProvider GraphQL",
        skip: !enrolledInGraphqlExperiment,
        subscriptionQuery: graphqlSubscriptionsQueries.provisioningStatusQuery,
        orchestrationId,
        onErrorCb: (_error) => {
            setEnrolledInGraphqlExperiment(false);
        }
    });

    // GraphQL experiment to listen to status change
    /* istanbul ignore next */
    useEffect(() => {
        if (!enrolledInGraphqlExperiment || graphqlProvisionComplete) {
            return;
        }
        // @ts-ignore
        fireFrontendProvisioningCompletedEvent(
            "graphql",
            productKey,
            edition,
            createAnalyticsEvent
        );
        if (!!subscriptionData) {
            // SLO "good event" for GraphQL subscription
            ufoEventGraphQLSubscriptionSetup.success({
                customData: {
                    reactComponent: "SignupProvisioningContextProvider GraphQL",
                    analyticsEvent: "succeeded",
                    orchestrationId
                }
            });
            ufoEventRedirectToProduct.endSubstep(GRAPHQL_SUBSCRIPTION_ACK);
        }
        try {
            // Guard to redirect users who bookmarked Confirmation page
            ufoEventGraphQLGetProvisioningStatus.start({ productKey });
            if (
                !!getProvisioningCloudStatus &&
                provisionPollingTimes.current === 0
            ) {
                const before = Date.now();
                getProvisioningCloudStatus()
                    .then((ready) => {
                        ufoEventGraphQLGetProvisioningStatus.setCustomData({
                            isInitialStatusPollingStatusOk: true
                        });
                        if (ready) {
                            setGraphqlProvisionComplete(true);
                            onSuccess(before, Date.now(), Date.now() - before);
                        }
                    })
                    .catch((_error) => {
                        // Do not throw on initial polling failure. Wait for GraphQL response
                        ufoEventGraphQLGetProvisioningStatus.setCustomData({
                            isInitialStatusPollingStatusOk: false
                        });
                    });
            }
            ufoEventRedirectToProduct.startSubstep(
                GRAPHQL_WEBHOOK_PROVISIONING_LATENCY
            );
            if (subscriptionData?.signup?.signupProvisioningStatus) {
                const {
                    provisionComplete,
                    orchestrationId: orchestrationIdFromSubscription,
                    cloudId
                } = subscriptionData.signup.signupProvisioningStatus;
                setCloudIdIfPresent(cloudId);
                if (provisionComplete && orchestrationIdFromSubscription) {
                    if (analyticsClient) {
                        const elapsedTimeSeconds = Math.round(
                            graphqlProvisioningMS.current / 1000
                        );

                        // @ts-ignore
                        trackGetProvisioningStatusLatencyEvent({
                            createAnalyticsEvent,
                            productKey,
                            fetchType: "graphql-webhook",
                            elapsedTimeSeconds: elapsedTimeSeconds
                        });

                        if (elapsedTimeSeconds > 35) {
                            const event = createAnalyticsEvent({
                                event: "succeeded",
                                eventName:
                                    "provisioningStatusPollingLatencyAbove35s",
                                eventType: "track",
                                action: "loaded",
                                actionSubject:
                                    "getProvisioningStatusLatency greater than 35s",
                                attributes: {
                                    elapsedTimeSeconds,
                                    productKey,
                                    provisioningScreenOnComplete:
                                        "provisioning-screen",
                                    fetchType: "graphql-webhook"
                                },
                                containerId: "perceivedProvisioningPage"
                            });
                            event.fire();
                        }
                    }

                    // SLO "good event" for GraphQL get provisioning status
                    ufoEventGraphQLGetProvisioningStatus.success({
                        customData: {
                            reactComponent:
                                "SignupProvisioningContextProvider GraphQL",
                            analyticsEvent: "succeeded",
                            elapsedTimeMs: graphqlProvisioningMS.current,
                            orchestrationId: orchestrationIdFromSubscription
                        }
                    });
                    setGraphqlProvisionComplete(true);
                    onSuccess(
                        currentTime,
                        Date.now(),
                        graphqlProvisioningMS.current
                    );
                }
            }
            if (subscriptionError) {
                // SLO "bad event" for GraphQL subscription
                ufoEventGraphQLSubscriptionSetup.failure({
                    error: subscriptionError,
                    customData: {
                        reactComponent:
                            "SignupProvisioningContextProvider GraphQL",
                        analyticsEvent: "errored",
                        errorOrigin: "useSubscription, useEffect",
                        orchestrationId
                    }
                });
                throw subscriptionError;
            }
        } catch (error) {
            // SLO "bad event" for GraphQL get provisioning status
            ufoEventGraphQLGetProvisioningStatus.failure({
                error,
                customData: {
                    reactComponent: "SignupProvisioningContextProvider GraphQL",
                    analyticsEvent: "errored",
                    elapsedTimeMs: graphqlProvisioningMS.current,
                    errorOrigin: "useEffect",
                    orchestrationId
                }
            });
            ufoEventRedirectToProduct.endSubstep(
                GRAPHQL_WEBHOOK_PROVISIONING_LATENCY,
                { customData: { orchestrationId } }
            );
            onError(
                currentTime,
                Date.now(),
                graphqlProvisioningMS.current,
                error
            );
            // fallback to control status polling
            setEnrolledInGraphqlExperiment(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        subscriptionData,
        subscriptionLoading,
        subscriptionError,
        getProvisioningCloudStatus,
        enrolledInGraphqlExperiment,
        graphqlProvisionComplete,
        createAnalyticsEvent,
        orchestrationId
    ]);

    // GraphQL experiment timeout listener
    const fallbackGraphqlToStatusPolling = useCallback(() => {
        if (!enrolledInGraphqlExperiment || graphqlProvisionComplete) {
            return;
        }
        // SLO "bad event" for GraphQL webhook provisioning status
        ufoEventGraphQLGetProvisioningStatus.failure({
            error: new Error("GraphQL timeout"),
            customData: {
                reactComponent: "SignupProvisioningContextProvider",
                analyticsEvent: "timedOut",
                orchestrationId
            }
        });
        // fallback to control status polling
        setEnrolledInGraphqlExperiment(false);
        return;
    }, [
        enrolledInGraphqlExperiment,
        graphqlProvisionComplete,
        orchestrationId
    ]);
    /* istanbul ignore next */
    useEffect(() => {
        if (!enrolledInGraphqlExperiment || graphqlProvisionComplete) {
            return;
        }
        const intervalId = setInterval(() => {
            if (graphqlProvisioningMS.current > GRAPHQL_FALLBACK_TIMEOUT) {
                fallbackGraphqlToStatusPolling();
                return;
            }
            graphqlProvisioningMS.current += 1000;
        }, 1000);
        return () => clearInterval(intervalId);
    }, [
        enrolledInGraphqlExperiment,
        graphqlProvisionComplete,
        platform,
        orchestrationId
    ]);

    useEffect(() => {
        if (enrolledInGraphqlExperiment || startPolling) {
            return;
        }
        // Add product cohort to UFO event (ie. metrics by product)
        const productKey = products.length > 0 ? products[0] : undefined;
        // Start UFO to measure the total duration and reliability of provisioning status polling
        ufoEventConfirmationPageStatusPolling.start({
            productKey: productKey as ProductKey,
            webPlatform: "bxp-ui"
        });

        const ufoSubstepKey =
            platform === "ccp"
                ? ACTIVATE_STATUS_CCP_POLLING_API
                : CLOUD_STATUS_HAMS_POLLING_API;
        const ufoReqPath =
            platform === "ccp"
                ? "/activate/status/:provisioningRequestId"
                : "/cofs/:cloudId/status/:requestId";
        ufoEventRedirectToProduct.startSubstep(ufoSubstepKey);
        setStartPolling(true);
        pollCloudStatus({
            getCloudStatus: getProvisioningCloudStatus,
            onError: (startTime, endTime, elapsedTime, error) => {
                onError(startTime, endTime, elapsedTime, error);
                // GasV3 event to measure polling provisioning latency
                // @ts-ignore
                trackGetProvisioningStatusLatencyEvent({
                    createAnalyticsEvent,
                    productKey: "confluence.ondemand",
                    fetchType: "polling",
                    elapsedTimeSeconds: Math.round(elapsedTime / 1000)
                });

                // SLO "bad event" for provisioning status polling
                ufoEventConfirmationPageStatusPolling.failure({
                    error,
                    customData: {
                        reactComponent: "SignupProvisioningContextProvider",
                        analyticsEvent: "errored",
                        elapsedTimeMs: elapsedTime
                    }
                });
                ufoEventRedirectToProduct.endSubstep(ufoSubstepKey, {
                    error,
                    customData: {
                        reqPath: ufoReqPath,
                        elapsedTimeMs: elapsedTime,
                        errorOrigin: "SignupProvisioningContextProvider onError"
                    }
                });
            },
            onSuccess: (startTime, endTime, elapsedTime) => {
                onSuccess(startTime, endTime, elapsedTime);
                // @ts-ignore
                fireFrontendProvisioningCompletedEvent(
                    "polling",
                    productKey,
                    edition,
                    createAnalyticsEvent
                );
                // GasV3 event to measure polling provisioning latency
                // @ts-ignore
                trackGetProvisioningStatusLatencyEvent({
                    createAnalyticsEvent,
                    productKey: "confluence.ondemand",
                    fetchType: "polling",
                    elapsedTimeSeconds: Math.round(elapsedTime / 1000)
                });
                // SLO "good event" for provisioning status polling
                ufoEventConfirmationPageStatusPolling.success({
                    customData: {
                        reactComponent: "SignupProvisioningContextProvider",
                        analyticsEvent: "succeeded",
                        elapsedTimeMs: elapsedTime
                    }
                });
                ufoEventRedirectToProduct.endSubstep(ufoSubstepKey, {
                    customData: {
                        reqPath: ufoReqPath,
                        elapsedTimeMs: elapsedTime
                    }
                });
            },
            onTimeout: (startTime, endTime, elapsedTime) => {
                onTimeout(startTime, endTime, elapsedTime);
                // GasV3 event to measure polling provisioning latency
                // @ts-ignore
                trackGetProvisioningStatusLatencyEvent({
                    createAnalyticsEvent,
                    productKey: "confluence.ondemand",
                    fetchType: "polling",
                    elapsedTimeSeconds: Math.round(elapsedTime / 1000)
                });
                // SLO "bad event" for provisioning status polling
                ufoEventConfirmationPageStatusPolling.failure({
                    error: new Error("Polling timeout"),
                    customData: {
                        reactComponent: "SignupProvisioningContextProvider",
                        analyticsEvent: "timedOut",
                        elapsedTimeMs: elapsedTime
                    }
                });
                ufoEventRedirectToProduct.endSubstep(ufoSubstepKey, {
                    error: new Error("Polling timeout"),
                    customData: {
                        reqPath: ufoReqPath,
                        elapsedTimeMs: elapsedTime,
                        errorOrigin:
                            "SignupProvisioningContextProvider onTimeout"
                    }
                });
            },
            startTime: perceivedProvisioningStartTimeRef.current ?? Date.now()
        });
    }, [
        startPolling,
        products,
        platform,
        getProvisioningCloudStatus,
        createAnalyticsEvent,
        enrolledInGraphqlExperiment
    ]);

    const onSuccess = useCallback(
        (startTime: number, endTime: number, elapsedTime: number) => {
            setStatus("success");
            createAnalyticsEvent({
                action: "cloudStatus",
                event: "succeeded",
                eventName: "createCloudSucceeded",
                startTime,
                endTime,
                elapsedTime
            }).fire("signup");
        },
        [createAnalyticsEvent]
    );

    const onError = useCallback(
        (
            startTime: number,
            endTime: number,
            elapsedTime: number,
            error: unknown
        ) => {
            setStatus("error");
            createAnalyticsEvent({
                action: "cloudStatus",
                event: "errored",
                eventName: "createCloudErrored",
                startTime,
                endTime,
                elapsedTime,
                error
            }).fire("signup");
        },
        [createAnalyticsEvent]
    );

    const onTimeout = useCallback(
        (startTime: number, endTime: number, elapsedTime: number) => {
            setStatus("timeout");
            createAnalyticsEvent({
                action: "cloudStatus",
                event: "timedOut",
                eventName: "createCloudTimedOut",
                startTime,
                endTime,
                elapsedTime
            }).fire("signup");
        },
        [createAnalyticsEvent]
    );

    return (
        <SignupProvisioningContext.Provider
            value={{
                startTime,
                status,
                parameters: searchParameters,
                createRedirectUrl,
                ccpData: {
                    cloudId: ccpProvisioningCloudId
                },
                perceivedProvisioningStartTime,
                setPerceivedProvisioningStartTime,
                onSuccess,
                onError,
                onTimeout
            }}
        >
            {children}
        </SignupProvisioningContext.Provider>
    );
};
